import type { Nullable } from "@core/domain/types/nullable.type";
import { BaseViewModel } from "@iot-platform/core";
import { PredefinedAlerts } from "@station/domain/models/predefined-alert.model";
import { CreatedPredefinedAlertGetAllUseCase } from "@station/domain/usecases/created-predefined-alert-get-all.usecase";
import { PredefinedAlertGetAllUseCase } from "@station/domain/usecases/predefined-alert-get-all.usecase";
import { PredefinedAlertUpdateUseCase } from "@station/domain/usecases/predefined-alert-update.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class PredefinedAlertSelectViewmodel extends BaseViewModel {
    @observable
    stationId: Nullable<string> = null;

    @observable
    predefinedAlerts: PredefinedAlerts = PredefinedAlerts.empty();

    @observable
    createdPredefinedAlerts: PredefinedAlerts = PredefinedAlerts.empty();

    @observable
    initialLoading = true;

    constructor(
        @inject(PredefinedAlertGetAllUseCase)
        private readonly predefinedAlertGetAllUseCase: PredefinedAlertGetAllUseCase,
        @inject(CreatedPredefinedAlertGetAllUseCase)
        private readonly createdPredefinedAlertGetAllUseCase: CreatedPredefinedAlertGetAllUseCase,
        @inject(PredefinedAlertUpdateUseCase)
        private readonly predefinedAlertUpdateUseCase: PredefinedAlertUpdateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([
            this.fetchPredefinedAlerts(),
            this.fetchCreatedPredefinedAlerts(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    private async fetchPredefinedAlerts(): Promise<void> {
        if (this.stationId) {
            const predefinedAlerts =
                await this.predefinedAlertGetAllUseCase.execute(this.stationId);

            runInAction(() => {
                this.predefinedAlerts = predefinedAlerts;
            });
        }
    }

    @action
    private async fetchCreatedPredefinedAlerts(): Promise<void> {
        if (this.stationId) {
            const createdPredefinedAlerts =
                await this.createdPredefinedAlertGetAllUseCase.execute(
                    this.stationId,
                );

            runInAction(() => {
                this.createdPredefinedAlerts = createdPredefinedAlerts;
            });
        }
    }

    @action
    async updatePredefinedAlerts(
        selectedPredefinedAlerts: number[],
    ): Promise<void> {
        if (this.stationId) {
            await this.predefinedAlertUpdateUseCase.execute(
                this.stationId,
                selectedPredefinedAlerts,
            );
            await this.reloadPredefinedAlerts();
        }
    }

    @action
    setStationId(stationId: string): void {
        runInAction(() => {
            this.stationId = stationId;
        });
    }

    @action
    async reloadPredefinedAlerts(): Promise<void> {
        await this.fetchCreatedPredefinedAlerts();
    }
}
